import React, { Suspense } from 'react';

import { LoadingSpinner } from 'components/Common/Spinner';
import { Toaster } from 'components/ui/sonner';
import 'react-app-polyfill/ie11';
// For IE 11 support
import 'react-app-polyfill/stable';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import QueryClient from 'react-query/QueryClient';
import QueryClientProvider from 'react-query/QueryClientProvider';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import { theme } from 'shared/constants';
import { AdminRequired, LoginRequired, NonLoginRequired } from 'shared/hocs';
import { lazyWithRetry } from 'shared/lib/lazyRetry';
import { ThemeProvider } from 'styled-components';

import packageJson from '../package.json';
// 프리렌더(scripts/prerender.js) 대상 공개 라우트는 하이드레이션 시 동기 렌더가 되도록
// React.lazy 대신 정적 import 한다. (lazy면 하이드레이션 첫 렌더에서 Suspense fallback이 떠
// 프리렌더된 실제 HTML과 어긋나 React #418/#423 하이드레이션 에러가 난다.)
import DefaultLayout from './app/DefaultLayout';
import { GlobalStyle } from './components/styles';
import './index.css';
// 라우터 errorElement — 청크가 깨진 상황에서도 반드시 떠야 하므로 lazy 금지(정적 import)
import RouterErrorFallback from './pages/error/RouterErrorFallback';
import GuidePage from './pages/guide';
import Home from './pages/home';
import RegisterPage from './pages/register';

// 이외에 필요한 스타일 파일이 있다면 import 해주세요.

console.log(`%cNE(O)RDINARY Project Name : ${packageJson.name}`, 'color:yellow');
console.log(`%cNE(O)RDINARY Project Version : ${packageJson.version}`, 'color:yellow');
console.log(`%cNE(O)RDINARY Project Made by : ${packageJson.author}`, 'color:yellow');

console.log(`
███╗   ██╗███████╗ ██╗ ██████╗ ██╗ ██████╗ ██████╗ ██╗███╗   ██╗ █████╗ ██████╗ ██╗   ██╗
████╗  ██║██╔════╝██╔╝██╔═══██╗╚██╗██╔══██╗██╔══██╗██║████╗  ██║██╔══██╗██╔══██╗╚██╗ ██╔╝
██╔██╗ ██║█████╗  ██║ ██║   ██║ ██║██████╔╝██║  ██║██║██╔██╗ ██║███████║██████╔╝ ╚████╔╝
██║╚██╗██║██╔══╝  ██║ ██║   ██║ ██║██╔══██╗██║  ██║██║██║╚██╗██║██╔══██║██╔══██╗  ╚██╔╝
██║ ╚████║███████╗╚██╗╚██████╔╝██╔╝██║  ██║██████╔╝██║██║ ╚████║██║  ██║██║  ██║   ██║
╚═╝  ╚═══╝╚══════╝ ╚═╝ ╚═════╝ ╚═╝ ╚═╝  ╚═╝╚═════╝ ╚═╝╚═╝  ╚═══╝╚═╝  ╚═╝╚═╝  ╚═╝   ╚═╝
`);

// Pages (DefaultLayout/Home/Guide/Register는 프리렌더 라우트라 위에서 정적 import)
const Login = lazyWithRetry(() => import('./pages/login'));
const Page404 = lazyWithRetry(() => import('./pages/page404/Page404'));
const Page500 = lazyWithRetry(() => import('./pages/page500/Page500'));
const AdminLogin = lazyWithRetry(() => import('./pages/login/admin'));
const KakaoLogin = lazyWithRetry(() => import('./pages/login/kakaologin'));
const RoomListPage = lazyWithRetry(() => import('./pages/room/list'));
const RoomCreatePage = lazyWithRetry(() => import('./pages/room/edit'));
const ContractPage = lazyWithRetry(() => import('./pages/contract'));
const ContractedPage = lazyWithRetry(() => import('./pages/contract/contracted'));
const ProfileEditPage = lazyWithRetry(() => import('./pages/profile/edit'));
const DashboardPage = lazyWithRetry(() => import('./pages/dashboard'));
const UserManagePage = lazyWithRetry(() => import('./pages/user/manage'));
const ApprovalManagePage = lazyWithRetry(() => import('./pages/user/approval'));
const CouponPage = lazyWithRetry(() => import('./pages/coupon'));
const RewardPage = lazyWithRetry(() => import('./pages/reward'));
const WaitlistPage = lazyWithRetry(() => import('./pages/waitlist'));
const RequestRoomPage = lazyWithRetry(() => import('./pages/service/RequestRoomPage'));
const MembershipPage = lazyWithRetry(() => import('./pages/service/MembershipPage'));

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: Infinity } },
});

const app = (
  <HelmetProvider>
    <QueryClientProvider queryClient={queryClient}>
      <RecoilRoot>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          <Suspense fallback={LoadingSpinner}>
            <App />
          </Suspense>
          <Toaster />
        </ThemeProvider>
      </RecoilRoot>
    </QueryClientProvider>
  </HelmetProvider>
);

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const container = document.getElementById('root')!;
// 프리렌더된 HTML(scripts/prerender.js 산출)은 크롤러용 첫 응답·첫 페인트로만 쓰고,
// 클라이언트는 그 위에 새로 렌더한다. hydrateRoot를 쓰면 이 앱 구성(react-router 데이터
// 라우터 + styled-components)이 클라이언트-캡처 방식 프리렌더와 첫 렌더에서 어긋나
// React #418/#423(복구 가능하지만 콘솔 에러)을 내므로, createRoot로 깔끔하게 렌더한다.
// 공개 라우트는 정적 import(위)라 재렌더 시 스피너 플래시가 없다.
createRoot(container).render(app);

function App() {
  return (
    <RouterProvider
      router={createBrowserRouter([
        {
          path: '/',
          element: <DefaultLayout />,
          // 기본 에러화면("Unexpected Application Error!") 대체 — 그 문구가 구글에
          // /register 페이지 제목으로 색인된 사례 있음. noindex + 새로고침 안내 UI.
          errorElement: <RouterErrorFallback />,
          children: [
            {
              path: '/',
              element: <Home />,
            },
            {
              path: '/room',
              element: (
                <LoginRequired to="/login">
                  <RoomListPage />
                </LoginRequired>
              ),
            },
            {
              path: '/room/create/:id?',
              element: (
                <LoginRequired to="/login">
                  <RoomCreatePage />
                </LoginRequired>
              ),
            },
            {
              path: '/contract',
              element: (
                <LoginRequired to="/login">
                  <ContractPage />
                </LoginRequired>
              ),
            },
            {
              path: '/contracted',
              element: (
                <LoginRequired to="/login">
                  <ContractedPage />
                </LoginRequired>
              ),
            },
            {
              path: '/service/request-a-room',
              element: (
                <LoginRequired to="/login">
                  <RequestRoomPage />
                </LoginRequired>
              ),
            },
            {
              path: '/service/membership',
              element: (
                <LoginRequired to="/login">
                  <MembershipPage />
                </LoginRequired>
              ),
            },
            {
              path: '/guide',
              element: <GuidePage />,
            },
            {
              path: '/profile/edit',
              element: (
                <LoginRequired to="/login">
                  <ProfileEditPage />
                </LoginRequired>
              ),
            },
            {
              path: '/dashboard',
              element: (
                <AdminRequired to="/">
                  <DashboardPage />
                </AdminRequired>
              ),
            },
            {
              path: '/user/manage',
              element: (
                <AdminRequired to="/">
                  <UserManagePage />
                </AdminRequired>
              ),
            },
            {
              path: '/user/approval',
              element: (
                <AdminRequired to="/">
                  <ApprovalManagePage />
                </AdminRequired>
              ),
            },
            {
              path: '/login',
              element: (
                <NonLoginRequired>
                  <Login />
                </NonLoginRequired>
              ),
            },
            {
              path: '/register',
              element: (
                <NonLoginRequired>
                  <RegisterPage />
                </NonLoginRequired>
              ),
            },
            {
              path: '/admin/login',
              element: (
                <NonLoginRequired>
                  <AdminLogin />
                </NonLoginRequired>
              ),
            },
            {
              path: '/coupon',
              element: (
                <AdminRequired to="/">
                  <CouponPage />
                </AdminRequired>
              ),
            },
            {
              path: '/reward',
              element: (
                <AdminRequired to="/">
                  <RewardPage />
                </AdminRequired>
              ),
            },
            {
              path: '/service/waitlist',
              element: (
                <AdminRequired to="/">
                  <WaitlistPage />
                </AdminRequired>
              ),
            },
            {
              path: '*',
              element: <Page404 />,
            },
          ],
        },
        {
          path: '/kakaologin',
          element: <KakaoLogin />,
          errorElement: <RouterErrorFallback />,
        },
        {
          path: '/404',
          element: <Page404 />,
          errorElement: <RouterErrorFallback />,
        },
        {
          path: '/500',
          element: <Page500 />,
          errorElement: <RouterErrorFallback />,
        },
      ])}
    />
  );
}
